import React, { useEffect, useRef, useState } from "react";
import {
    Container,
    Header,
    RatingSection,
    Title,
    Description,
    DescriptionRow,
    FeedbackBlock,
    FeedbackLabel,
    FeedbackText,
    UploadTime,
    Footer,
    FooterMeta,
    PackageDialog,
    PackageDialogContent,
    PackageDialogDelimiter,
    Highlight,
    EmbargoBadge
} from "./Package.elements";
import StatusPieChart from "./StatusPieChart";
import { PackageDetails } from "./PackageDetails";
import { StarFilled, StarOutlined } from "@/icons";
import {
    ModalHeader,
    ModalHeaderCloseButton,
} from "../Util/Modal.elements";
import { formatEmbargo, formatEmbargoDate, isEmbargoActive } from "@/utils/embargo";

const PackageSection = ({
                            uploadMetadataId,
                            upload,
                            packageName,
                            description,
                            contributorName,
                            dateShot,
                            status,
                            feedback,
                            stackFeedback = false,
                        }) => {
    const [isOpen, setIsOpen] = useState(false);
    const contentRef = useRef(null);

    // Red outline + glued badge for as long as the embargo has not passed.
    const embargoActive = isEmbargoActive(upload?.embargo);
    // Badge carries the date only; the exact time stays on the tooltip and in the
    // package details metadata.
    const embargoBadgeText = embargoActive
        ? `Embargoed till ${formatEmbargoDate(upload?.embargo)}`
        : "";
    const embargoTooltip = embargoActive
        ? `Embargoed until ${formatEmbargo(upload?.embargo)}`
        : "";

    useEffect(() => {
        if (!isOpen) return;
        requestAnimationFrame(() => {
            if (contentRef.current) contentRef.current.scrollTop = 0;
        });
    }, [isOpen]);

    const renderStars = (rating) => {
        const filled = Number.isFinite(rating) ? Math.max(0, Math.min(5, rating)) : 0;
        return Array.from({ length: 5 }, (_, i) =>
            i + 1 <= filled ? <StarFilled key={i} /> : <StarOutlined key={i} />
        );
    };

    return (
        <>
            <Container onClick={() => setIsOpen(true)} $embargoed={embargoActive}>
                {embargoActive && (
                    <EmbargoBadge title={embargoTooltip}>
                        {embargoBadgeText}
                    </EmbargoBadge>
                )}

                <Header>
                    <Title>{packageName}</Title>
                    <RatingSection>{renderStars(upload?.rating)}</RatingSection>
                </Header>

                <DescriptionRow $stackFeedback={stackFeedback}>
                    <Description>{description}</Description>
                    {feedback != null && (
                        <FeedbackBlock $stackFeedback={stackFeedback}>
                            <FeedbackLabel>Feedback</FeedbackLabel>
                            <FeedbackText>{feedback}</FeedbackText>
                        </FeedbackBlock>
                    )}
                </DescriptionRow>

                <Footer style={{ justifyContent: "space-between", paddingRight: "0px" }}>
                    <FooterMeta $stackFeedback={stackFeedback}>
                        <StatusPieChart status={status} />
                        <UploadTime $stackFeedback={stackFeedback}>
                            Uploaded by <Highlight>{contributorName}</Highlight> on <Highlight>{dateShot}</Highlight> CEST
                        </UploadTime>
                    </FooterMeta>
                </Footer>
            </Container>

            <PackageDialog open={isOpen} onClose={() => setIsOpen(false)}>
                <PackageDialogContent ref={contentRef}>
                    <ModalHeader>
                        <ModalHeaderCloseButton type="button" onClick={() => setIsOpen(false)}>
                            &times;
                        </ModalHeaderCloseButton>
                    </ModalHeader>

                    {/* No embargo badge or outline inside the dialog: the details view
                        already calls the embargo out in red in its metadata strip. */}
                    <Container>
                        <Header>
                            <Title>{packageName}</Title>
                            <RatingSection>{renderStars(upload?.rating)}</RatingSection>
                        </Header>

                        <DescriptionRow $stackFeedback={stackFeedback}>
                            <Description>{description}</Description>
                            {feedback != null && (
                                <FeedbackBlock $stackFeedback={stackFeedback}>
                                    <FeedbackLabel>Feedback</FeedbackLabel>
                                    <FeedbackText>{feedback}</FeedbackText>
                                </FeedbackBlock>
                            )}
                        </DescriptionRow>

                        <Footer style={{ justifyContent: "space-between", paddingRight: "0px" }}>
                            <StatusPieChart status={status} />
                            <UploadTime>
                                Uploaded by <Highlight>{contributorName}</Highlight> on <Highlight>{dateShot}</Highlight> CEST
                            </UploadTime>
                        </Footer>
                    </Container>

                    <PackageDialogDelimiter />
                    <PackageDetails uploadMetadataId={uploadMetadataId} upload={upload} />
                </PackageDialogContent>
            </PackageDialog>
        </>
    );
};

export default PackageSection;